import React from 'react';
import { Link } from 'react-router-dom';
import { ContactForm } from './ContactForm';
import { FooterWordmark } from './FooterWordmark';
import { GridRails } from './GridRails';
import { GridDivider } from './GridDivider';
import { Reveal } from './Reveal';
import { FillLabel } from './FillLabel';
import { PdfDownloadButton } from './PdfDownloadButton';
import { footerLinks, legalLinks, contacts } from '../data/navigation';
import { manuscriptMeta } from '../data/manuscript';
import { ecosystemPillars } from '../data/ecosystem';

// Linked ventures only — the brands with a live address
const brands = ecosystemPillars.flatMap(p => p.ventures).filter((v): v is typeof v & {
  url: string;
} => !!v.url);
const inner = 'relative z-10 mx-auto max-w-[1720px] px-9 md:px-24 lg:px-32';
const label = "flex items-center gap-3 font-accent text-[11px] uppercase tracking-[0.08em] text-cream/80 before:inline-block before:h-[6px] before:w-[6px] before:shrink-0 before:bg-white before:content-['']";
const linkCls = 'link-soft';

/** Navy footer (hero colour): contact, link columns, manuscript, huge wordmark, legal row. */
export function Footer() {
  return <footer id="contact" className="relative bg-navy text-cream" data-id="element-142" data-mp="3gctpopi22rrv">
      <GridRails tone="dark" data-id="element-143" data-mp="3v9b11gq8w6ef" />

      {/* Top: contact, boxed by full-width lines */}
      <ContactForm data-id="element-144" data-mp="3q7llpovzp6kj" />

      {/* Links */}
      <Reveal className={`${inner} grid grid-cols-2 gap-x-8 gap-y-10 py-12 md:grid-cols-4 md:gap-y-12 md:py-16`} data-id="element-145" data-mp="3qazr75zy9vw7">
        <nav aria-label="Footer" data-id="element-146" data-mp="33xchtnnhqqe">
          <h3 className={label} data-id="element-147" data-mp="32w27regl89q">Navigate</h3>
          <ul className="mt-5 space-y-2.5 text-[14px] text-cream" data-id="element-148" data-mp="31umh9mhzxlo">
            {footerLinks.map(l => <li key={l} data-id="element-149" data-mp="huo4p2t5mru1">
                <a href="#" className={linkCls} data-id="element-150" data-mp="ct77pidsjpiq">{l}</a>
              </li>)}
          </ul>
        </nav>

        <div data-id="element-151" data-mp="qujmhqh7tl31">
          <h3 className={label} data-id="element-152" data-mp="qub4xla5drli">Ecosystem Brands</h3>
          <ul className="mt-5 space-y-2.5 text-[14px] text-cream" data-id="element-153" data-mp="ms6fyzun9j57">
            {brands.map(b => <li key={b.name} data-id="element-154" data-mp="tvd0bu5d69g7">
                <a href={b.url} target="_blank" rel="noopener noreferrer" className={linkCls} data-id="element-155" data-mp="twfp9wj4m1th">
                  {b.name}
                </a>
              </li>)}
          </ul>
        </div>

        <div data-id="element-156" data-mp="13siq8z28rueu">
          <h3 className={label} data-id="element-157" data-mp="13rpoydm1xx43">Contact</h3>
          <ul className="mt-5 space-y-4 text-[14px] text-cream" data-id="element-158" data-mp="18jr17zj7znu4">
            {contacts.map(c => <li key={c.email} data-id="element-159" data-mp="1clumffvoy6sl">
                <span className="block text-[12px] text-cream/50" data-id="element-160" data-mp="1ckt02f985xvk">{c.label}</span>
                <a href={`mailto:${c.email}`} className={linkCls} data-id="element-161" data-mp="1hjw7258rme66">
                  {c.email}
                </a>
              </li>)}
          </ul>
        </div>

        <div data-id="element-162" data-mp="1rilfz8ioooaa">
          <h3 className={label} data-id="element-163" data-mp="1rjemq9hn7dsf">Follow</h3>
          <ul className="mt-5 space-y-2.5 text-[14px] text-cream" data-id="element-164" data-mp="1ri3lohfl0k50">
            <li data-id="element-165" data-mp="3oc6s71c6n613">
              <a href="#" className={linkCls} data-id="element-166" data-mp="3ocpyfksh05za">LinkedIn</a>
            </li>
            <li data-id="element-167" data-mp="3t9gfaac3n5lr">
              <a href="https://kays.vc" className={linkCls} data-id="element-168" data-mp="3tb19tzyeyojm">kays.vc</a>
            </li>
          </ul>
        </div>
      </Reveal>

      <GridDivider tone="dark" data-id="element-169" data-mp="2ah87722jtky5" />

      {/* Manuscript strip — a slightly lifted navy band */}
      <div id="manuscript" className="scroll-mt-24 bg-[#18263f]" data-id="element-170" data-mp="25mamuyztjp5y">
      <div className={`${inner} flex flex-col gap-6 py-9 md:flex-row md:items-center md:justify-between`} data-id="element-171" data-mp="21nv3r66uz0xt">
        <Reveal className="flex flex-col gap-3 md:flex-row md:items-center md:gap-10" data-id="element-172" data-mp="1wp09n83fxpye">
          <h3 className="whitespace-nowrap font-display text-[28px] font-normal leading-[0.96] tracking-[-0.04em] md:text-[34px]" data-id="element-173" data-mp="2tcyaqtq4qpe5">
            <span className="font-editorial italic tracking-[-0.02em]" data-id="element-174" data-mp="2pd5uduuhvey8">0/1</span> Manuscript
          </h3>
          <p className="max-w-[320px] text-[13px] leading-[1.45] text-cream/60" data-id="element-175" data-mp="2kfe4t7hbo4x7">
            A short text on the balance between zero and one — stillness and action — and why quality compounds.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="flex flex-wrap items-center gap-3" data-id="element-176" data-mp="13z1kzdt5ruu9">
          <Link to="/manuscript" className="btn-fill btn-fill-light inline-flex h-[48px] items-center whitespace-nowrap border border-cream/30 px-6 font-accent text-[11px] uppercase tracking-[0.08em] text-cream/90" data-id="element-177" data-mp="1414ieiwn4q90">
            <FillLabel data-id="element-178" data-mp="2ivugd6pffdh4">Read online</FillLabel>
          </Link>
          <PdfDownloadButton href={manuscriptMeta.pdf} data-id="element-179" data-mp="2nu5o9bw5vdwp" />
        </Reveal>
      </div>
      </div>

      <GridDivider tone="dark" data-id="element-180" data-mp="3bjqkgwhbs7nx" />

      {/* Wordmark */}
      <Reveal className={`${inner} pb-14 pt-16 md:pb-20 md:pt-20`} data-id="element-181" data-mp="3bl1jyk3qmt98">
        <FooterWordmark data-id="element-182" data-mp="3flttzfkfp26k" />
      </Reveal>

      <GridDivider tone="dark" data-id="element-183" data-mp="3fkiuhry0ugl9" />

      {/* Legal row — closes the page. Plain block: at the very bottom it can never
          cross the reveal trigger line, so an entrance animation would keep it hidden. */}
      <div className={`${inner} flex flex-col gap-4 py-7 text-[12px] md:flex-row md:items-center md:justify-between`} data-id="element-184" data-mp="36mqeelkh74wt">
        <p className="text-cream/55" data-id="element-185" data-mp="1v5gu5wtiw8ug">© {new Date().getFullYear()} KAYS &amp; KAYS · Dubai, United Arab Emirates. All rights reserved.</p>
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-cream/80" data-id="element-186" data-mp="1nkwhs5zeb6fc">
          {legalLinks.map(l => <li key={l.href} data-id="element-187" data-mp="1im334a8dh9dl">
              <Link to={l.href} className={`${linkCls} no-arrow`} data-id="element-188" data-mp="1ioz281vyi43m">{l.label}</Link>
            </li>)}
        </ul>
      </div>
    </footer>;
}